import { useEffect, useRef, useState } from 'react';
import { IconClose } from './icons';
import { useSwipeClose } from './SwipeSheet';
import { calculateAndApplyCartUpdates } from './utils';

interface Props {
  apiUrl: string;
  liveSessionId?: string;
  onClose: () => void;
}

declare global {
  interface Window {
    IntanDiscountCalculator?: {
      mount: (
        container: HTMLElement | string,
        config?: { apiUrl?: string; containerSelector?: string; liveSessionId?: string }
      ) => (() => void) | undefined;
      unmount: (container: HTMLElement | string) => void;
      init: (config?: { apiUrl?: string; containerSelector?: string; liveSessionId?: string }) => void;
    };
  }
}

/**
 * Store cart as a modal over LIVE (stream keeps playing behind).
 * Loads / mounts the same intan-discount-cart.js bundle used on /cart.
 */
export function CartModal({ apiUrl, liveSessionId, onClose }: Props) {
  const mountRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const swipeClose = useSwipeClose(onClose);
  const handleClose = () => swipeClose();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose]);

  useEffect(() => {
    let cancelled = false;
    let unmountFn: (() => void) | undefined;
    const mountEl = mountRef.current;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        await calculateAndApplyCartUpdates(apiUrl, undefined, liveSessionId).catch(() => undefined);

        // Snapshot nodes that exist before we (maybe) load the discount-cart script,
        // so we can strip only mounts created by a buggy auto-init — never theme embeds.
        const preexisting = snapshotDiscountCartNodes();
        const alreadyLoaded = Boolean(window.IntanDiscountCalculator);
        await ensureDiscountCartScript();
        if (!alreadyLoaded) {
          removeNewlyCreatedDiscountCartMounts(preexisting, mountRef.current);
        }

        if (cancelled || !mountRef.current) return;

        const calc = window.IntanDiscountCalculator;
        if (!calc) {
          throw new Error('intan-discount-cart.js failed to load');
        }

        if (typeof calc.mount === 'function') {
          const fn = calc.mount(mountRef.current, { apiUrl, liveSessionId });
          if (cancelled) {
            fn?.();
            return;
          }
          unmountFn = fn;
        } else {
          // Older bundle: init into our container via selector
          mountRef.current.id = 'intan-live-discount-cart';
          mountRef.current.setAttribute('data-intan-discount-container', '');
          calc.init({ apiUrl, containerSelector: '#intan-live-discount-cart', liveSessionId });
          unmountFn = () => {
            try {
              calc.unmount?.('#intan-live-discount-cart');
            } catch {
              mountRef.current && (mountRef.current.innerHTML = '');
            }
          };
          if (cancelled) {
            unmountFn();
            unmountFn = undefined;
            return;
          }
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Failed to open cart');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      try {
        unmountFn?.();
      } catch {
        // ignore
      }
      // Belt-and-suspenders if async mount raced past cancelled without storing unmountFn
      if (mountEl) {
        try {
          window.IntanDiscountCalculator?.unmount?.(mountEl);
        } catch {
          // ignore
        }
        mountEl.innerHTML = '';
      }
    };
  }, [apiUrl, liveSessionId]);

  return (
    <div className="il-cart-modal" role="dialog" aria-label="Shopping cart">
      <div className="il-cart-modal-bar">
        <span>Shopping cart</span>
        <button type="button" className="il-page-icon" onClick={handleClose} aria-label="Close">
          <IconClose />
        </button>
      </div>
      <div className="il-cart-modal-body">
        {loading && <p className="il-cart-loading">Loading cart…</p>}
        {error && <p className="il-error-dark">{error}</p>}
        <div ref={mountRef} className="il-cart-mount" data-intan-live-cart />
      </div>
    </div>
  );
}

function snapshotDiscountCartNodes(): Set<Element> {
  return new Set(
    document.querySelectorAll('#intan-discount-calculator, [data-intan-discount-container]')
  );
}

/** Drop mounts auto-created when LIVE loaded intan-discount-cart.js. */
function removeNewlyCreatedDiscountCartMounts(
  preexisting: Set<Element>,
  keep: HTMLElement | null
) {
  const calc = window.IntanDiscountCalculator;
  document
    .querySelectorAll<HTMLElement>('#intan-discount-calculator, [data-intan-discount-container]')
    .forEach((el) => {
      if (preexisting.has(el)) return;
      if (keep && (el === keep || keep.contains(el) || el.contains(keep))) return;
      if (el.closest('.il-cart-mount, .il-cart-modal, [data-intan-live-cart]')) return;
      try {
        calc?.unmount?.(el);
      } catch {
        // ignore
      }
      el.remove();
    });
}

function ensureDiscountCartScript(): Promise<void> {
  if (window.IntanDiscountCalculator) return Promise.resolve();

  const existing = document.querySelector<HTMLScriptElement>(
    'script[src*="intan-discount-cart"]'
  );
  if (existing) {
    return waitForCalculator(existing);
  }

  const src = resolveDiscountCartScriptUrl();
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    // Do not set data-intan-api-url — that triggers theme auto-init.
    // LIVE mounts explicitly via IntanDiscountCalculator.mount().
    script.onload = () => {
      if (window.IntanDiscountCalculator) resolve();
      else reject(new Error('IntanDiscountCalculator missing after script load'));
    };
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

function waitForCalculator(script: HTMLScriptElement): Promise<void> {
  if (window.IntanDiscountCalculator) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const done = () => {
      if (window.IntanDiscountCalculator) resolve();
      else reject(new Error('IntanDiscountCalculator missing after script load'));
    };
    script.addEventListener('load', done, { once: true });
    script.addEventListener('error', () => reject(new Error('Failed to load cart bundle')), {
      once: true,
    });
    // Script may already be loaded but global not ready yet
    let tries = 0;
    const poll = window.setInterval(() => {
      tries += 1;
      if (window.IntanDiscountCalculator) {
        clearInterval(poll);
        resolve();
      } else if (tries > 40) {
        clearInterval(poll);
        reject(new Error('Timed out waiting for IntanDiscountCalculator'));
      }
    }, 50);
  });
}

function resolveDiscountCartScriptUrl(): string {
  const liveScript = document.querySelector<HTMLScriptElement>(
    'script[src*="intan-live-commerce"]'
  );
  if (liveScript?.src) {
    return liveScript.src.replace(/intan-live-commerce(\.js)?([?#].*)?$/i, 'intan-discount-cart.js');
  }

  const fromData = document
    .querySelector('[data-intan-discount-cart-src]')
    ?.getAttribute('data-intan-discount-cart-src');
  if (fromData) return fromData;

  return 'https://intan-v2-aa5ba.web.app/shopify/intan-discount-cart.js';
}
